"use client";

import { Clock } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";
import { type SupportedLocale, getLocaleConfig } from "@/config/locale-config";
import { SigningErrorFooter } from "./signing-error-footer";
import { SigningErrorIcon } from "./signing-error-icon";

type Props = {
	onTryAgain: () => void;
	onCancelAndSave: () => void;
	/** A retry is already redirecting to the identity provider. */
	isRetrying?: boolean;
};

export const SigningSessionExpired = ({
	onTryAgain,
	onCancelAndSave,
	isRetrying = false,
}: Props) => {
	const t = useTranslations("pages.SignCallbackPage");
	const tips = t.raw("sessionExpired.tips") as string[];
	const locale = useLocale() as SupportedLocale;
	const { supportPhone: phone } = getLocaleConfig(locale);

	return (
		<StepContainer>
			<Stack gap={3} align="stretch">
				<Stack gap={2} align="center" className="text-center text-balance">
					<SigningErrorIcon>
						<Clock size={40} strokeWidth={2} aria-hidden="true" />
					</SigningErrorIcon>
					<Heading level={1} textStyle="heading-lg">
						{t("sessionExpired.title")}
					</Heading>
					<Text color="text.subtle">{t("sessionExpired.description")}</Text>
				</Stack>

				<div className="rounded-xl bg-bg-subtle p-2.5">
					<Text
						render={<span />}
						fontWeight="medium"
						color="text.base"
						className="mb-1.5 block uppercase"
					>
						{t("sessionExpired.tipsTitle")}
					</Text>
					<Stack render={<ol />} gap={1.25} className="m-0 list-decimal pl-2.5">
						{tips.map((tip) => (
							<Text render={<li />} textStyle="body-sm" key={tip}>
								{tip}
							</Text>
						))}
					</Stack>
				</div>

				<Stack gap={1.5}>
					<Button
						onClick={onTryAgain}
						disabled={isRetrying}
						aria-busy={isRetrying}
						className="w-full"
					>
						{t("sessionExpired.tryAgain")}
					</Button>
					<Button
						variant="outline"
						onClick={onCancelAndSave}
						className="w-full"
					>
						{t("sessionExpired.cancelAndSave")}
					</Button>
				</Stack>

				<SigningErrorFooter text={t("footer", { phone })} />
			</Stack>
		</StepContainer>
	);
};
